import {
  diffStreetSurveyExtractionRecord,
  getModeLabel,
  STREET_SURVEY_BATCH_MAX_POINTS,
  STREET_SURVEY_LEGACY_CALLS_PER_POINT,
  STREET_SURVEY_V2_CALLS_PER_POINT,
} from '@citizen-walk/shared-types';
import { Button, Card, ConfirmDialog, Modal, useToast } from '@citizen-walk/shared-ui';
import { httpsCallable } from 'firebase/functions';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { functions } from '../../lib/firebase';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import type { LatLng } from '../bulk-photo-import';
import { useStreetSurveyPosts, type StreetSurveyPost } from './useStreetSurveyPosts';
import { useStreetSurveyConfig } from './useStreetSurveyConfig';
import { StreetSurveyItemsSelector } from './StreetSurveyItemsSelector';
import { StreetSurveyExtractionPanel } from './StreetSurveyExtractionPanel';
import { useStreetViewBaseHeading } from './useStreetViewBaseHeading';
import { StreetSurveyResultsMap } from './StreetSurveyResultsMap';
import { StreetSurveyImageBoard } from './StreetSurveyImageBoard';
import { StreetSurveyPanorama } from './StreetSurveyPanorama';
import { StreetSurveyUploadPanorama } from './StreetSurveyUploadPanorama';
import { StreetSurveyActionBar } from './StreetSurveyActionBar';
import { StreetSurveyNewPointPreview } from './StreetSurveyNewPointPreview';
import { StreetSurveyInsightPanel } from './StreetSurveyInsightPanel';
import { StreetSurveyBatchPanel } from './StreetSurveyBatchPanel';
import { StreetSurveySurveysPanel } from './StreetSurveySurveysPanel';
import { useStreetSurveySelection } from './streetSurveySurveys';
import { StreetSurveyPhotoLibraryPanel } from './StreetSurveyPhotoLibraryPanel';
import { AreaReportModal } from './AreaReportModal';
import { RouteReportModal } from './RouteReportModal';
import { useStreetSurveyBatchJob } from './useStreetSurveyBatchJob';
import { computeAxisCounts, computeEvaluationSummary } from './computeAreaAxisCounts';
import { saveRouteReportSnapshot } from './routeReportSnapshotActions';
import { saveAreaReportSnapshot } from './areaReportSnapshotActions';
import { deleteStreetSurveyPost } from './streetSurveyPostActions';
import { buildRouteFromSelection, polygonForSelection } from './selectionGeometry';

/**
 * ストリートビュー机上調査 (docs/25、Increment 2 = 結果地図 + 画像ボード + エリア集計、
 * Increment 3 = base_heading自動検出 + 3Dパノラマ重畳)。
 *
 * 地図は1つ（`StreetSurveyResultsMap`）に統合されている。既存解析済み地点の閲覧・投げ縄
 * エリア選択・新規地点解析の座標ピックの3モードを同じ地図上で `interactionMode` により
 * 排他的に切り替える（新規地点用に別マップを開く旧UIは廃止、地図の行き来を無くすため）。
 *
 * baseHeading は `useStreetViewBaseHeading` (`getPanorama` 経由) が検出できた場合のみ渡す。
 * 地区で3D機能が無効、または検出失敗時は callable 側の 0 固定フォールバックに委ねる。
 *
 * docs/27 (2026-09-07): 登録済み 360度写真 (`provider='upload-360'`) の一覧・複数選択・一括解析・
 * 路線 / エリア登録 (`StreetSurveyPhotoLibraryPanel`) と、未解析点のプレビュー (見回し表示) を追加。
 * 地図の多角形選択は `interactionMode='selectPoints'` で「解析対象の選択」として再利用する。
 */

type InteractionMode = 'view' | 'pick' | 'lasso' | 'line' | 'routeSelect' | 'selectPoints';

interface AnalyzeStreetViewResult {
  ok: boolean;
  postId?: string;
  deduped?: boolean;
  error?: string;
}

interface AnalyzeStreetViewBatchResult {
  ok: boolean;
  jobId?: string;
  error?: string;
}

/** `analyzeStreetViewBatch` の 1 ジョブ上限 (functions と shared-types の定数を共有)。 */
const MAX_BATCH_POINTS = STREET_SURVEY_BATCH_MAX_POINTS;

type RegisterKind = 'route' | 'area';

export function StreetSurveyPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const districtState = useDistrict(districtId ?? null);
  const { state: authState } = useAdminAuth();
  const { toast } = useToast();

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  // docs/25 §10-1 地区別の実施可否ゲート。この地区で机上調査が有効化されているか (analysisEnabled)、
  // 有効なら分析の実行 (課金) が許可されているか (master は常に可、地区管理者は master が
  // districtAdminCanAnalyze を ON にした地区のみ)。閲覧は featureEnabled であれば地区管理者も可。
  const streetViewConfig =
    districtState.status === 'ready' ? districtState.district.streetView : undefined;
  const featureEnabled = streetViewConfig?.analysisEnabled === true;
  const canRun =
    featureEnabled &&
    (isMaster || (isDistrictAdminOfThis && streetViewConfig?.districtAdminCanAnalyze === true));

  const district = districtState.status === 'ready' ? districtState.district : null;
  // docs/98 R-97: 表示・集計・解析する調査 (URL の ?survey=)。
  const surveySelection = useStreetSurveySelection(district);
  const surveyKey = surveySelection.surveyKey;
  const surveyPostsState = useStreetSurveyPosts(districtId, district, surveyKey);
  const posts = useMemo(
    () => (surveyPostsState.status === 'ready' ? surveyPostsState.posts : []),
    [surveyPostsState],
  );
  // 登録済み 360度写真 (docs/27)。一覧・選択の対象。

  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [interactionMode, setInteractionMode] = useState<InteractionMode>('view');
  const [newLocation, setNewLocation] = useState<LatLng | null>(null);
  const [busy, setBusy] = useState(false);
  const [areaReportIds, setAreaReportIds] = useState<string[] | null>(null);
  // エリア選択に使った多角形 (2026-07-16、AreaReportModal のPDFエクスポート地図用)。
  const [areaReportPolygon, setAreaReportPolygon] = useState<LatLng[] | null>(null);
  const [viewMode, setViewMode] = useState<'2d' | '3d'>('2d');
  const [highlightedRank, setHighlightedRank] = useState<number | null>(null);
  const [drawnLine, setDrawnLine] = useState<LatLng[] | null>(null);
  // 「既存地点から路線を作成」モードで描いた線 (docs/25 §10-6 拡張、2026-07-21)。新規AI解析は
  // 行わず、既存の解析済み地点から近接するものを検出して路線として保存する。
  const [routeDrawnLine, setRouteDrawnLine] = useState<LatLng[] | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  // パネルを閉じても進捗追跡を失わないよう、jobId はパネルではなくここで保持する
  // (2026-07-16、常時表示の進捗バッジ用にも共有する)。
  const [batchJobId, setBatchJobId] = useState<string | null>(null);
  const batchJob = useStreetSurveyBatchJob(districtId ?? '', batchJobId);

  // docs/27 §7: 登録済み 360度写真の複数選択 (解析対象 / 路線・エリア登録 / 削除)。
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [register, setRegister] = useState<{
    kind: RegisterKind;
    posts: StreetSurveyPost[];
  } | null>(null);
  const [registerName, setRegisterName] = useState('');
  const [registering, setRegistering] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState<StreetSurveyPost[] | null>(null);
  const [deleting, setDeleting] = useState(false);

  const selectedPost = posts.find((p) => p.id === selectedPostId) ?? null;
  // 同一地区内の SV post は同じ地区 AI 設定を共有する (docs/25) ため、選択中 post が無くても
  // 一覧の先頭 post の settingsId で primary axis を解決できる (エリア集計は選択前でも開けるため)。
  // 未解析の登録済み写真しか無い地区では post に settingsId が無いため、地区の AI 設定にフォールバック。
  const primarySettingsId =
    selectedPost?.settingsId ??
    posts.find((p) => p.settingsId)?.settingsId ??
    district?.aiAnalysis?.settingsId ??
    null;
  // docs/27 §9: 調査項目 (分類軸) と評価軸を、地区の上書きを反映した実効設定から解決する。
  const surveyConfig = useStreetSurveyConfig(district, primarySettingsId, surveyKey);
  // 表示名が内部のキーと同じ (custom モードで表示名が未設定) ときは出さない。
  const surveyModeLabel = district?.mode ? getModeLabel(district.mode, district.modeLabel) : '';
  const surveyModeName = surveyModeLabel !== district?.mode ? surveyModeLabel : '';
  const [legendAxisKeyState, setLegendAxisKey] = useState<string | null>(null);
  const [aggregateAxisKeyState, setAggregateAxisKey] = useState<string | null>(null);
  const legendAxisKey = legendAxisKeyState ?? surveyConfig.defaultLegendAxisKey;
  const aggregateAxisKey = aggregateAxisKeyState ?? surveyConfig.defaultAggregateAxisKey;
  // 既存コンポーネントの型に合わせる: 集計軸 = primary 形、凡例軸 = role 形 (色分け優先)。
  const primaryAxis = useMemo(
    () => surveyConfig.asPrimary(aggregateAxisKey),
    [surveyConfig, aggregateAxisKey],
  );
  const roleTypeAxis = useMemo(
    () => surveyConfig.asRole(legendAxisKey),
    [surveyConfig, legendAxisKey],
  );
  const evaluation = surveyConfig.evaluation;
  // docs/98 R-96 #4: 現在の設定と違う設定・プロンプトで解析された地点を、エリア / 路線の集計から除く切替。
  // 地図・一覧には出したまま、集計 (エリア集計・路線作成・選択からの登録・一括解析の路線保存) だけに効く。
  const outdatedPostIds = useMemo(() => {
    const current = surveyConfig.extraction;
    if (!current.enabled) return new Set<string>();
    return new Set(
      posts
        .filter(
          (p) =>
            p.analysisStatus === 'done' &&
            diffStreetSurveyExtractionRecord(
              p.streetView?.extraction,
              current,
              surveyConfig.mastersById,
            ).length > 0,
        )
        .map((p) => p.id),
    );
  }, [posts, surveyConfig.extraction, surveyConfig.mastersById]);
  const [excludeOutdated, setExcludeOutdated] = useState(false);
  const excludingOutdated = excludeOutdated && outdatedPostIds.size > 0;
  const aggregationPosts = useMemo(
    () => (excludingOutdated ? posts.filter((p) => !outdatedPostIds.has(p.id)) : posts),
    [posts, excludingOutdated, outdatedPostIds],
  );

  // 新規地点解析フォームの地図は、いつも日本全体から始めて地区までズームし直すのが手間なため、
  // 地区の投稿可能エリア中心 (postAreaCenter、既存フィールド) があればそこを初期中心にする。
  const districtDefaultCenter =
    districtState.status === 'ready' && districtState.district.postAreaCenter
      ? {
          latitude: districtState.district.postAreaCenter.latitude,
          longitude: districtState.district.postAreaCenter.longitude,
        }
      : undefined;

  const districtStreetViewEnabled =
    districtState.status === 'ready' && districtState.district.streetView?.enabled === true;
  const baseHeadingState = useStreetViewBaseHeading(
    districtId ?? null,
    districtStreetViewEnabled,
    newLocation,
  );

  async function handleRunNewPoint() {
    if (!districtId || !canRun || !newLocation || busy) return;
    setBusy(true);
    try {
      // サーバ側 analyzeStreetView の timeoutSeconds:300 に合わせる。SDK既定70sのままだと
      // maxOutputTokens次第で生成が長引いた際、解析自体は成功しているのにクライアント側だけ
      // deadline-exceeded で失敗表示になる (post は作成される)。
      const callable = httpsCallable<
        { districtId: string; lat: number; lng: number; heading?: number; surveyKey?: string },
        AnalyzeStreetViewResult
      >(functions, 'analyzeStreetView', { timeout: 300000 });
      const res = await callable({
        districtId,
        lat: newLocation.latitude,
        lng: newLocation.longitude,
        // docs/98 R-97: 追加の調査を選んでいれば、その調査で新しい地点を解析する。
        ...(surveySelection.isDefault ? {} : { surveyKey }),
        ...(baseHeadingState.status === 'detected' ? { heading: baseHeadingState.heading } : {}),
      });
      if (res.data.ok && res.data.postId) {
        setSelectedPostId(res.data.postId);
        setInteractionMode('view');
        setNewLocation(null);
        toast(
          'success',
          res.data.deduped
            ? '既に近傍 (10m以内) で解析済みの地点があります。既存の結果を表示します。'
            : '解析が完了しました。',
        );
      } else {
        toast('error', `解析失敗: ${res.data.error ?? '不明なエラー'}`);
      }
    } catch (e) {
      toast('error', `analyzeStreetView 失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /** docs/27 §7: 選択した登録済み 360度写真を一括解析 (postId 指定のバッチジョブ)。 */
  async function handleAnalyzeSelected(targets: StreetSurveyPost[]) {
    if (!districtId || !canRun || busy || targets.length === 0) return;
    const slice = targets.slice(0, MAX_BATCH_POINTS);
    setBusy(true);
    try {
      const callable = httpsCallable<
        {
          districtId: string;
          points: { lat: number; lng: number; postId: string }[];
          surveyKey?: string;
        },
        AnalyzeStreetViewBatchResult
      >(functions, 'analyzeStreetViewBatch');
      const res = await callable({
        districtId,
        ...(surveySelection.isDefault ? {} : { surveyKey }),
        points: slice.map((p) => ({
          lat: p.location.latitude,
          lng: p.location.longitude,
          postId: p.id,
        })),
      });
      if (res.data.ok && res.data.jobId) {
        setBatchJobId(res.data.jobId);
        toast(
          'success',
          targets.length > slice.length
            ? `${slice.length} 件の解析を開始しました（1回の上限 ${MAX_BATCH_POINTS} 件。残り ${targets.length - slice.length} 件は完了後に再度実行してください）。`
            : `${slice.length} 件の解析を開始しました。進捗は画面右下に表示されます。`,
        );
        setSelectedIds(new Set());
      } else {
        toast('error', `解析の開始に失敗しました: ${res.data.error ?? '不明なエラー'}`);
      }
    } catch (e) {
      toast('error', `解析の開始に失敗しました: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /** docs/27 §8: 選択を路線 / エリアとして登録 (スナップショット方式、全件解析済みが前提)。 */
  async function handleRegister() {
    if (!districtId || !register || !authState.user || registering) return;
    const name = registerName.trim();
    if (!name) return;
    const targets = register.posts;
    // 集計から除く設定なら、設定の違う地点は集計に入れない (路線の形は選んだ地点すべてで作る)。
    const aggregated = excludingOutdated
      ? targets.filter((p) => !outdatedPostIds.has(p.id))
      : targets;
    setRegistering(true);
    try {
      const postIds = aggregated.map((p) => p.id);
      const common = {
        districtId,
        name,
        postIds,
        pointCount: aggregated.length,
        axisCounts: computeAxisCounts(aggregated, primaryAxis, evaluation),
        axisKey: aggregateAxisKey,
        evaluation: computeEvaluationSummary(aggregated, evaluation),
        summary: null,
        surveyKey,
      };
      if (register.kind === 'route') {
        const route = buildRouteFromSelection(targets);
        await saveRouteReportSnapshot(
          { ...common, lineCoordinates: route.lineCoordinates, lengthM: route.lengthM },
          authState.user.uid,
        );
        toast(
          'success',
          `路線「${name}」として登録しました（${targets.length} 地点、約 ${Math.round(route.lengthM)}m）。`,
        );
      } else {
        const polygon = polygonForSelection(targets.map((p) => p.location));
        await saveAreaReportSnapshot({ ...common, polygon }, authState.user.uid);
        toast('success', `エリア「${name}」として登録しました（${targets.length} 地点）。`);
      }
      setRegister(null);
      setRegisterName('');
    } catch (e) {
      toast('error', `登録に失敗しました: ${(e as Error).message}`);
    } finally {
      setRegistering(false);
    }
  }

  async function handleDeleteSelected() {
    if (!districtId || !deleteTargets || deleting) return;
    setDeleting(true);
    let failed = 0;
    try {
      for (const p of deleteTargets) {
        try {
          await deleteStreetSurveyPost(districtId, p.id, [p.photoPath, p.photoThumbPath]);
        } catch {
          failed++;
        }
      }
      const next = new Set(selectedIds);
      for (const p of deleteTargets) next.delete(p.id);
      setSelectedIds(next);
      if (deleteTargets.some((p) => p.id === selectedPostId)) setSelectedPostId(null);
      toast(
        failed > 0 ? 'error' : 'success',
        failed > 0
          ? `${deleteTargets.length - failed} 件を削除しました（${failed} 件は失敗）。`
          : `${deleteTargets.length} 件を削除しました。`,
      );
    } finally {
      setDeleting(false);
      setDeleteTargets(null);
    }
  }

  // 同じ要素を「地図の外 (通常時)」と「地図の中 (全画面表示中、children オーバーレイ)」の
  // 両方に置く (2026-07-16)。全画面表示中は地図の外側が一切見えなくなるため。
  const actionBar = districtId ? (
    <StreetSurveyActionBar
      districtId={districtId}
      selectedPost={selectedPost}
      canRun={canRun}
      onStartAreaSelect={() => setInteractionMode('lasso')}
      onOpenBatch={() => setBatchOpen(true)}
      onDeleted={() => setSelectedPostId(null)}
      surveyKey={surveyKey}
      linkQuery={surveySelection.querySuffix}
    />
  ) : null;

  const pageHeader = (
    <header className="mb-6">
      <Link
        to={districtId ? `/districts/${districtId}` : '/districts'}
        className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
      >
        ← 地区詳細
      </Link>
      <h1 className="text-2xl font-bold text-slate-900 mt-2">
        ストリートビュー机上調査
        {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
      </h1>
      {/* 調査の種類 (地区のモードの表示名) と適用中の机上調査テンプレート (docs/98 R-98 中)。
          custom モードで表示名が無いときは内部のキーを出さない。 */}
      {(surveyModeName || surveyConfig.template) && (
        <p className="text-xs text-slate-500 mt-1">
          {surveyModeName && `調査の種類: ${surveyModeName}`}
          {surveyModeName && surveyConfig.template && '・'}
          {surveyConfig.template && `机上調査テンプレート: ${surveyConfig.template.displayName}`}
        </p>
      )}
      <p className="text-sm text-slate-500 mt-1">
        座標を指定して Street View の4方向静止画を取得するか、登録済みの360°写真を選んで、この地区の
        AI 解析設定で机上調査を行います。
      </p>
    </header>
  );

  // docs/25 §10-1: この地区で机上調査が有効化されていない場合は、機能を出さず案内のみ表示する。
  if (districtState.status === 'ready' && !featureEnabled) {
    return (
      <>
        {pageHeader}
        <Card padding="md" className="border-amber-300 bg-amber-50">
          <p className="text-sm text-amber-800">
            この地区ではストリートビュー机上調査を利用できません。
            {isMaster
              ? '「AI 設定」ページの「ストリートビュー机上調査」から、この地区で実施可能にできます。'
              : '利用するにはマスター管理者による有効化が必要です。'}
          </p>
        </Card>
      </>
    );
  }

  const selectedIsUnanalyzedUpload =
    selectedPost?.provider === 'upload-360' && selectedPost.analysisStatus !== 'done';

  return (
    <>
      {pageHeader}

      {featureEnabled && !canRun && (
        <Card padding="md" className="border-amber-300 bg-amber-50 mb-4">
          <p className="text-sm text-amber-800">
            この地区での分析の実行はマスター管理者のみに許可されています。結果の閲覧はできますが、
            新規解析・一括解析・再解析はマスター管理者にご依頼ください。
          </p>
        </Card>
      )}

      {districtId && (
        <StreetSurveySurveysPanel
          districtId={districtId}
          district={district}
          selection={surveySelection}
          canManage={isMaster}
          uid={authState.user?.uid ?? null}
        />
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          variant={interactionMode === 'pick' ? 'primary' : 'secondary'}
          size="sm"
          disabled={!canRun}
          onClick={() => {
            setNewLocation(null);
            setInteractionMode((m) => (m === 'pick' ? 'view' : 'pick'));
          }}
        >
          {interactionMode === 'pick' ? '新規地点の選択をやめる' : '+ 新規地点を解析'}
        </Button>
        <Button
          variant={interactionMode === 'line' ? 'primary' : 'secondary'}
          size="sm"
          disabled={!canRun}
          onClick={() => {
            setDrawnLine(null);
            setInteractionMode((m) => (m === 'line' ? 'view' : 'line'));
          }}
        >
          {interactionMode === 'line' ? '線描画をやめる' : '地図で線を描く'}
        </Button>
        <Button
          variant={interactionMode === 'routeSelect' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => {
            setRouteDrawnLine(null);
            setInteractionMode((m) => (m === 'routeSelect' ? 'view' : 'routeSelect'));
          }}
        >
          {interactionMode === 'routeSelect' ? '路線描画をやめる' : '既存地点から路線を作成'}
        </Button>
      </div>

      {interactionMode === 'pick' && !newLocation && (
        <div className="mb-2 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded px-3 py-2">
          地図をクリックして解析したい地点を選んでください。
          <Button size="sm" variant="secondary" onClick={() => setInteractionMode('view')}>
            キャンセル
          </Button>
        </div>
      )}
      {interactionMode === 'lasso' && (
        <div className="mb-2 flex items-center gap-2 text-sm text-primary-700 bg-primary-50 rounded px-3 py-2">
          範囲の頂点をクリックして多角形を作成してください（3点以上）。地図下部の「確定」（またはダブルクリック）で選択します。
          <Button size="sm" variant="secondary" onClick={() => setInteractionMode('view')}>
            キャンセル
          </Button>
        </div>
      )}
      {interactionMode === 'selectPoints' && (
        <div className="mb-2 flex items-center gap-2 text-sm text-primary-700 bg-primary-50 rounded px-3 py-2">
          範囲の頂点をクリックして多角形を作成してください（3点以上）。確定すると範囲内の登録済み360°写真が選択に追加されます。
          <Button size="sm" variant="secondary" onClick={() => setInteractionMode('view')}>
            キャンセル
          </Button>
        </div>
      )}
      {interactionMode === 'line' && (
        <div className="mb-2 flex items-center gap-2 text-sm text-primary-700 bg-primary-50 rounded px-3 py-2">
          道路沿いに複数回クリックして頂点を追加してください（曲がりに沿って）。地図下部の「完了」（またはダブルクリック）で確定します。
          <Button size="sm" variant="secondary" onClick={() => setInteractionMode('view')}>
            キャンセル
          </Button>
        </div>
      )}
      {interactionMode === 'routeSelect' && (
        <div className="mb-2 flex items-center gap-2 text-sm text-primary-700 bg-primary-50 rounded px-3 py-2">
          道路沿いに複数回クリックして頂点を追加してください。地図下部の「完了」（またはダブルクリック）で確定すると、線の近くにある既存の解析済み地点を検出します（新規のAI解析は行いません）。
          <Button size="sm" variant="secondary" onClick={() => setInteractionMode('view')}>
            キャンセル
          </Button>
        </div>
      )}

      {districtId && (
        <StreetSurveyItemsSelector
          districtId={districtId}
          config={surveyConfig}
          legendAxisKey={legendAxisKey}
          aggregateAxisKey={aggregateAxisKey}
          onChangeLegend={setLegendAxisKey}
          onChangeAggregate={setAggregateAxisKey}
          canSaveDefault={isMaster}
          uid={authState.user?.uid ?? null}
          surveyKey={surveyKey}
          district={district}
        />
      )}
      {districtId && (
        // docs/27 §15-6: 解析の設定（新方式）。編集は master のみ (地区の AI 解析設定は master 専管)。
        <StreetSurveyExtractionPanel
          districtId={districtId}
          district={district}
          config={surveyConfig}
          posts={posts}
          canEdit={isMaster}
          uid={authState.user?.uid ?? null}
          surveyKey={surveyKey}
        />
      )}

      {outdatedPostIds.size > 0 && (
        <label className="mb-3 flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <input
            type="checkbox"
            checked={excludeOutdated}
            onChange={(e) => setExcludeOutdated(e.target.checked)}
          />
          現在の設定・プロンプトと違う内容で解析された {outdatedPostIds.size} 地点を、エリア /
          路線の集計から除く（地図と一覧には表示したままです）
        </label>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StreetSurveyResultsMap
          districtId={districtId}
          posts={posts}
          selectedPostId={selectedPostId}
          onSelectPost={setSelectedPostId}
          lassoEnabled={interactionMode === 'lasso' || interactionMode === 'selectPoints'}
          onLassoSelect={(ids, polygon) => {
            if (interactionMode === 'selectPoints') {
              // docs/27 §7: 範囲内の机上調査の地点を選択に追加する (エリア集計は開かない)。
              // 2026-09-16: 登録した 360度写真だけでなくストリートビュー由来の地点も選べる (一括再解析)。
              const next = new Set(selectedIds);
              let added = 0;
              for (const id of ids) {
                if (!next.has(id)) {
                  next.add(id);
                  added++;
                }
              }
              setSelectedIds(next);
              setInteractionMode('view');
              toast('success', `${added} 件を選択に追加しました（選択中 ${next.size} 件）。`);
              return;
            }
            setInteractionMode('view');
            if (ids.length > 0) {
              setAreaReportIds(ids);
              setAreaReportPolygon(polygon);
            }
          }}
          primaryAxis={primaryAxis}
          roleTypeAxis={roleTypeAxis}
          pickMode={interactionMode === 'pick'}
          pendingLocation={interactionMode === 'pick' ? newLocation : null}
          onPickLocation={setNewLocation}
          lineDrawEnabled={interactionMode === 'line'}
          onLineDrawn={(coords) => {
            setDrawnLine(coords);
            setInteractionMode('view');
            setBatchOpen(true);
          }}
          routeDrawEnabled={interactionMode === 'routeSelect'}
          onRouteDrawn={(coords) => {
            setRouteDrawnLine(coords);
            setInteractionMode('view');
          }}
          defaultCenter={districtDefaultCenter}
          multiSelectedIds={selectedIds}
        >
          {actionBar}
        </StreetSurveyResultsMap>
        {interactionMode === 'pick' ? (
          <Card padding="lg">
            <h2 className="text-sm font-bold text-slate-700 mb-2">座標を選ぶ</h2>
            {newLocation ? (
              <>
                <p className="text-xs text-slate-500">
                  緯度: {newLocation.latitude.toFixed(6)} / 経度: {newLocation.longitude.toFixed(6)}
                </p>
                {districtStreetViewEnabled && (
                  <p className="text-xs text-slate-500 mt-2">
                    {baseHeadingState.status === 'loading' && '道路方向を検出中…'}
                    {baseHeadingState.status === 'detected' &&
                      `検出した撮影基準方角: ${Math.round(baseHeadingState.heading)}°（自動検出、Google Maps）`}
                    {baseHeadingState.status === 'not-found' &&
                      'この地点周辺の道路方向を自動検出できませんでした（0°で解析します）'}
                    {baseHeadingState.status === 'disabled' &&
                      'この地区ではストリートビュー3D機能が無効なため、自動検出は行いません（0°で解析します）'}
                    {baseHeadingState.status === 'error' &&
                      '⚠ ブラウザ用Google Maps APIキーが未設定または無効なため、道路方向を自動検出できません（0°で解析します）。地区の「AI設定」ページでキー設定を確認してください。'}
                  </p>
                )}
                {districtId && (
                  <StreetSurveyNewPointPreview
                    districtId={districtId}
                    lat={newLocation.latitude}
                    lng={newLocation.longitude}
                    baseHeading={
                      baseHeadingState.status === 'detected' ? baseHeadingState.heading : 0
                    }
                  />
                )}
                <div className="flex justify-end mt-3">
                  <Button onClick={() => void handleRunNewPoint()} disabled={!canRun || busy}>
                    {busy ? '解析中…' : '解析実行'}
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500">地図をクリックして地点を選んでください。</p>
            )}
          </Card>
        ) : selectedPost && selectedIsUnanalyzedUpload ? (
          // docs/27 §7: 未解析の登録済み 360度写真は見回しプレビュー + 解析ボタン。
          <Card padding="md">
            <h2 className="text-sm font-bold text-slate-700 mb-1">
              登録済み360°写真（
              {selectedPost.analysisStatus === 'failed'
                ? '解析失敗'
                : selectedPost.analysisStatus === 'pending'
                  ? '解析中'
                  : '未解析'}
              ）
            </h2>
            <p className="text-xs text-slate-500 mb-2 flex flex-wrap gap-x-3">
              <span>{selectedPost.fileName ?? '(ファイル名なし)'}</span>
              {selectedPost.capturedAt && (
                <span>撮影 {selectedPost.capturedAt.toLocaleString('ja-JP')}</span>
              )}
              <span>
                座標 {selectedPost.location.latitude.toFixed(6)},{' '}
                {selectedPost.location.longitude.toFixed(6)}
              </span>
              {selectedPost.streetView && (
                <span>撮影基準方角 {Math.round(selectedPost.streetView.baseHeading)}°</span>
              )}
            </p>
            <StreetSurveyUploadPanorama
              post={selectedPost}
              primaryAxis={primaryAxis}
              roleTypeAxis={roleTypeAxis}
              hidePins
              className="w-full h-[380px] rounded-lg border border-slate-200"
            />
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <Button
                size="sm"
                variant="primary"
                disabled={!canRun || busy}
                onClick={() => void handleAnalyzeSelected([selectedPost])}
              >
                この写真をAI解析
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  const next = new Set(selectedIds);
                  if (next.has(selectedPost.id)) next.delete(selectedPost.id);
                  else next.add(selectedPost.id);
                  setSelectedIds(next);
                }}
              >
                {selectedIds.has(selectedPost.id) ? '選択から外す' : '選択に追加'}
              </Button>
            </div>
          </Card>
        ) : selectedPost?.streetView ? (
          <div>
            <div className="flex gap-2 mb-2">
              <Button
                size="sm"
                variant={viewMode === '2d' ? 'primary' : 'secondary'}
                onClick={() => setViewMode('2d')}
              >
                2D画像ボード
              </Button>
              <Button
                size="sm"
                variant={viewMode === '3d' ? 'primary' : 'secondary'}
                onClick={() => setViewMode('3d')}
              >
                3Dパノラマ
              </Button>
            </div>
            {viewMode === '2d' ? (
              <StreetSurveyImageBoard
                districtId={districtId!}
                post={selectedPost}
                primaryAxis={primaryAxis}
                roleTypeAxis={roleTypeAxis}
                highlightedRank={highlightedRank}
              />
            ) : selectedPost.provider === 'upload-360' ? (
              <StreetSurveyUploadPanorama
                post={selectedPost}
                primaryAxis={primaryAxis}
                roleTypeAxis={roleTypeAxis}
                highlightedRank={highlightedRank}
                onSelectRank={setHighlightedRank}
                reasoningField={surveyConfig.schema?.reasoningField}
              />
            ) : (
              <StreetSurveyPanorama
                districtId={districtId!}
                post={selectedPost}
                primaryAxis={primaryAxis}
                roleTypeAxis={roleTypeAxis}
                highlightedRank={highlightedRank}
                onSelectRank={setHighlightedRank}
                reasoningField={surveyConfig.schema?.reasoningField}
              />
            )}
          </div>
        ) : (
          <Card padding="md">
            <p className="text-sm text-slate-500">地図で地点を選択してください。</p>
          </Card>
        )}
      </div>

      {districtId && (
        <>
          {actionBar}
          <StreetSurveyBatchPanel
            districtId={districtId}
            isMaster={isMaster}
            open={batchOpen}
            initialLine={drawnLine}
            jobId={batchJobId}
            onJobStarted={setBatchJobId}
            onClose={() => {
              setBatchOpen(false);
              setDrawnLine(null);
            }}
            posts={aggregationPosts}
            primaryAxis={primaryAxis}
            evaluation={evaluation}
            axisKey={aggregateAxisKey}
            surveyKey={surveyKey}
            callsPerPoint={
              surveyConfig.extraction.enabled
                ? STREET_SURVEY_V2_CALLS_PER_POINT
                : STREET_SURVEY_LEGACY_CALLS_PER_POINT
            }
          />
        </>
      )}
      <StreetSurveyInsightPanel post={selectedPost} />

      {/* docs/27 §7: 机上調査の地点の一覧・選択 (登録した 360度写真 + ストリートビュー由来、1 件以上あるとき)。 */}
      {posts.length > 0 && (
        <StreetSurveyPhotoLibraryPanel
          posts={posts}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onPreview={(id) => setSelectedPostId(id)}
          previewPostId={selectedPostId}
          canRun={canRun}
          busy={busy || registering || deleting}
          onAnalyze={(targets) => void handleAnalyzeSelected(targets)}
          onRegisterRoute={(targets) => {
            setRegisterName('');
            setRegister({ kind: 'route', posts: targets });
          }}
          onRegisterArea={(targets) => {
            setRegisterName('');
            setRegister({ kind: 'area', posts: targets });
          }}
          onDelete={(targets) => setDeleteTargets(targets)}
          mapSelectActive={interactionMode === 'selectPoints'}
          onToggleMapSelect={() =>
            setInteractionMode((m) => (m === 'selectPoints' ? 'view' : 'selectPoints'))
          }
        />
      )}

      {areaReportIds && districtId && (
        <AreaReportModal
          districtId={districtId}
          postIds={areaReportIds}
          posts={aggregationPosts}
          surveyKey={surveyKey}
          detailQuery={surveySelection.querySuffix}
          primaryAxis={primaryAxis}
          roleTypeAxis={roleTypeAxis}
          polygon={areaReportPolygon}
          evaluation={evaluation}
          axisKey={aggregateAxisKey}
          surveyAxes={surveyConfig.axes}
          onClose={() => {
            setAreaReportIds(null);
            setAreaReportPolygon(null);
          }}
        />
      )}

      {routeDrawnLine && districtId && (
        <RouteReportModal
          districtId={districtId}
          posts={aggregationPosts}
          surveyKey={surveyKey}
          detailQuery={surveySelection.querySuffix}
          lineCoordinates={routeDrawnLine}
          primaryAxis={primaryAxis}
          roleTypeAxis={roleTypeAxis}
          evaluation={evaluation}
          axisKey={aggregateAxisKey}
          surveyAxes={surveyConfig.axes}
          onClose={() => setRouteDrawnLine(null)}
        />
      )}

      {/* docs/27 §8: 選択を路線 / エリアとして登録 (名前入力)。 */}
      <Modal
        open={register !== null}
        onClose={() => (registering ? undefined : setRegister(null))}
        title={register?.kind === 'route' ? '路線として登録' : 'エリアとして登録'}
        closeOnOverlayClick={false}
      >
        {register && (
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              選択した <span className="font-semibold">{register.posts.length} 地点</span>{' '}
              {register.kind === 'route'
                ? 'を撮影時刻順（無ければファイル名順）に結んだ折れ線を路線として保存します。'
                : 'を囲む範囲をエリアとして保存します。'}
              集計値（検出件数・評価の内訳）は現在の解析結果で固定されます。
              {excludingOutdated &&
                register.posts.some((p) => outdatedPostIds.has(p.id)) &&
                `設定の違う ${register.posts.filter((p) => outdatedPostIds.has(p.id)).length} 地点は集計に入れません。`}
            </p>
            <label className="block">
              <span className="text-slate-600">名前</span>
              <input
                type="text"
                value={registerName}
                onChange={(e) => setRegisterName(e.target.value)}
                placeholder={register.kind === 'route' ? '例: 海岸通り（南）' : '例: 駅前エリア'}
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                autoFocus
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={registering}
                onClick={() => setRegister(null)}
              >
                キャンセル
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={registering || registerName.trim() === ''}
                onClick={() => void handleRegister()}
              >
                {registering ? '登録中…' : '登録'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={deleteTargets !== null}
        title="選択した地点を削除しますか？"
        message={`${deleteTargets?.length ?? 0} 件の登録済み360°写真（写真ファイルと解析結果）を削除します。この操作は取り消せません。`}
        confirmLabel={deleting ? '削除中…' : '削除する'}
        intent="danger"
        onConfirm={() => void handleDeleteSelected()}
        onCancel={() => (deleting ? undefined : setDeleteTargets(null))}
      />

      {/* 一括解析の進捗バッジ (2026-07-16): パネルを閉じても jobId をここで保持し続けるため、
          モーダルを閉じている間もここに常時表示される。クリックでパネルを再度開ける。 */}
      {batchJobId && !batchOpen && (
        <div className="fixed bottom-4 right-4 z-40 w-72 rounded-lg border border-slate-200 bg-white shadow-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <button
              type="button"
              className="text-sm font-semibold text-slate-700 hover:underline text-left"
              onClick={() => setBatchOpen(true)}
            >
              一括解析
              {batchJob?.status === 'done'
                ? ' — 完了'
                : batchJob?.status === 'failed'
                  ? ' — 失敗'
                  : '実行中…'}
            </button>
            <button
              type="button"
              aria-label="進捗表示を閉じる"
              className="text-slate-400 hover:text-slate-600 text-xs ml-2"
              onClick={() => setBatchJobId(null)}
            >
              ✕
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-1">
            {batchJob?.progress ?? 0} / {batchJob?.points.length ?? '?'} 件
          </p>
          <div className="w-full bg-slate-200 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${
                batchJob?.status === 'failed' ? 'bg-danger-600' : 'bg-primary-500'
              }`}
              style={{
                width: `${batchJob && batchJob.points.length > 0 ? (batchJob.progress / batchJob.points.length) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
